import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode
} from 'react'
import { useTranslation } from 'react-i18next'

/** Toast notification types */
type ToastType = 'success' | 'error' | 'warning' | 'info'

/** Undo action callback */
type UndoAction = () => void | Promise<void>

/** Toast notification data */
interface Toast {
  id: string
  message: string
  type: ToastType
  onUndo?: UndoAction
}

/** Options for toast with undo */
interface ToastWithUndoOptions {
  message: string
  onUndo: UndoAction
  duration?: number
  undoLabel?: string
}

/** Toast context value */
interface ToastContextValue {
  toasts: Toast[]
  addToast: (message: string, type?: ToastType, duration?: number) => string
  removeToast: (id: string) => void
  success: (message: string, duration?: number) => string
  error: (message: string, duration?: number) => string
  warning: (message: string, duration?: number) => string
  info: (message: string, duration?: number) => string
  /** Show a toast with an undo action - great for destructive operations */
  withUndo: (options: ToastWithUndoOptions) => string
}

/** Props for ToastProvider */
interface ToastProviderProps {
  children: ReactNode
}

/** Props for ToastContainer */
interface ToastContainerProps {
  toasts: Toast[]
  removeToast: (id: string) => void
}

/** Props for ToastItem */
interface ToastItemProps {
  toast: Toast
  onClose: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string): void => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  // Generate unique ID to avoid collisions
  const idCounter = useRef(0)
  const generateId = useCallback((): string => {
    idCounter.current += 1
    return `${Date.now()}-${idCounter.current}-${Math.random().toString(36).slice(2, 9)}`
  }, [])

  const addToast = useCallback((
    message: string,
    type: ToastType = 'info',
    duration = 4000,
    onUndo?: UndoAction
  ): string => {
    const id = generateId()
    const toast: Toast = { id, message, type, onUndo }

    setToasts(prev => [...prev, toast])

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [removeToast])

  const withUndo = useCallback(({ message, onUndo, duration = 6000 }: ToastWithUndoOptions): string => {
    return addToast(message, 'info', duration, onUndo)
  }, [addToast])

  const success = useCallback((message: string, duration?: number): string => {
    return addToast(message, 'success', duration)
  }, [addToast])

  const error = useCallback((message: string, duration?: number): string => {
    return addToast(message, 'error', duration)
  }, [addToast])

  const warning = useCallback((message: string, duration?: number): string => {
    return addToast(message, 'warning', duration)
  }, [addToast])

  const info = useCallback((message: string, duration?: number): string => {
    return addToast(message, 'info', duration)
  }, [addToast])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<ToastContextValue>(() => ({
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    withUndo
  }), [toasts, addToast, removeToast, success, error, warning, info, withUndo])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps): JSX.Element | null {
  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 flex flex-col gap-2 sm:left-auto sm:right-4 sm:w-96">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onClose }: ToastItemProps): JSX.Element {
  const { t } = useTranslation()
  const typeStyles: Record<ToastType, string> = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-600 text-white'
  }

  const icons: Record<ToastType, JSX.Element> = {
    success: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  const handleUndo = async () => {
    if (toast.onUndo) {
      await toast.onUndo()
      onClose()
    }
  }

  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-lg shadow-lg ${typeStyles[toast.type]} animate-slide-up`}
      role="alert"
    >
      {icons[toast.type]}
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      {toast.onUndo && (
        <button
          onClick={handleUndo}
          className="px-3 py-1 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded transition-colors"
          aria-label={t('toast.undoAction')}
        >
          {t('toast.undo')}
        </button>
      )}
      <button
        onClick={onClose}
        className="p-1 hover:opacity-80 transition-opacity"
        aria-label={t('toast.closeNotification')}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export default ToastContext
